import { BaseClient } from '../base-client';
import type { SystemRole } from '../system-roles/types';
import type {
  BusinessUnit,
  BusinessUnitListItem,
  BusinessUnitStanding,
  ChangePrimaryEmployerInput,
  CreateBusinessUnitInput,
  GrantAccessInput,
  PatchBusinessUnitInput,
  UnitBasics,
  UnitSummary,
} from './types';

const unit = (id: string) => `/api/business-units/${encodeURIComponent(id)}`;

/**
 * Business units. Access to a unit changes only through the explicit
 * operations — `grant`, `changeRole`, `revoke`, `changePrimaryEmployer` —
 * never by saving a list.
 */
export class BusinessUnitsClient extends BaseClient {
  /** The units the caller can see — every unit for whoever manages business
   *  units — and what each holds. */
  list(): Promise<{ data: BusinessUnitListItem[] }> {
    return this.request('/api/business-units');
  }

  /** The same units, names only — for pickers (no counts computed). */
  names(): Promise<{ data: UnitBasics[] }> {
    return this.request('/api/business-units', { query: { counts: 'false' } });
  }

  /** The units one employee can act in (of those the caller can see). */
  ofUser(userId: string): Promise<{ data: BusinessUnitStanding[] }> {
    return this.request(`/api/business-units/of-user/${encodeURIComponent(userId)}`);
  }

  create(input: CreateBusinessUnitInput): Promise<{ data: BusinessUnit }> {
    return this.request('/api/business-units', { method: 'POST', body: input });
  }

  patch(id: string, input: PatchBusinessUnitInput): Promise<{ data: BusinessUnit }> {
    return this.request(unit(id), { method: 'PATCH', body: input });
  }

  /** The unit's own roles — the choices when granting or changing a role. */
  roles(id: string): Promise<{ data: SystemRole[] }> {
    return this.request(`${unit(id)}/roles`);
  }

  /** The unit's page: details, counts, and who manages it. */
  summary(id: string): Promise<{ data: UnitSummary }> {
    return this.request(`${unit(id)}/summary`);
  }

  grant(id: string, input: GrantAccessInput): Promise<{ data: unknown }> {
    return this.request(`${unit(id)}/access`, { method: 'POST', body: input });
  }

  changeRole(id: string, userId: string, systemRoleId: string): Promise<{ data: null }> {
    return this.request(`${unit(id)}/access/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: { systemRoleId },
    });
  }

  revoke(id: string, userId: string): Promise<{ data: null }> {
    return this.request(`${unit(id)}/access/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
  }

  /** Make unit `id` the employee's primary employer. */
  changePrimaryEmployer(id: string, input: ChangePrimaryEmployerInput): Promise<{ data: unknown }> {
    return this.request(`${unit(id)}/primary-employer`, { method: 'POST', body: input });
  }
}
